import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { LibraryList, AudioPlayer } from '../components/library';
import { useConversions } from '../hooks';
import { Conversion } from '../types';
import { conversionsApi } from '../api/conversions';
import { trackEvent } from '../lib/analytics';
import { Button, SectionHeader } from '../components/common';
import AddToFeedModal from '../components/feed/AddToFeedModal';

export function LibraryPage() {
  const {
    conversions,
    loading,
    error,
    total,
    currentPage,
    pageSize,
    fetchConversions,
    deleteConversion,
    refreshConversion,
  } = useConversions({ limit: 10 });

  const [playingConversion, setPlayingConversion] = useState<Conversion | null>(null);
  const [playingAudioUrl, setPlayingAudioUrl] = useState<string | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const playRequestRef = useRef(0);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [feedPickerOpen, setFeedPickerOpen] = useState(false);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const enterSelectionMode = () => {
    setPlayingConversion(null);
    setPlayingAudioUrl(null);
    setSelectionMode(true);
  };

  const handlePlay = async (conversion: Conversion) => {
    trackEvent('audio_play', { surface: 'collection' });
    const requestId = ++playRequestRef.current;
    setPlaybackError(null);
    try {
      const { audioUrl } = await conversionsApi.getAudioUrl(conversion.id);
      if (requestId !== playRequestRef.current) return;
      setPlayingAudioUrl(audioUrl);
      setPlayingConversion(conversion);
    } catch (err) {
      console.error('Failed to load audio for conversion:', conversion.id, err);
      if (requestId !== playRequestRef.current) return;
      setPlayingConversion(null);
      setPlayingAudioUrl(null);
      setPlaybackError(
        `${conversion.title || 'This conversion'}: ${err instanceof Error ? err.message : 'Failed to load audio'}`
      );
    }
  };

  const handlePageChange = (page: number) => {
    fetchConversions(page);
  };

  const isPlayerVisible = !!(playingConversion && playingAudioUrl);
  const showSelectionToolbar = selectionMode;

  return (
    <Layout>
      <div
        className={`max-w-4xl mx-auto animate-fade-in ${isPlayerVisible || showSelectionToolbar ? 'pb-32' : ''}`}
      >
        <SectionHeader
          eyebrow="collection"
          title="Your collection"
          description="Every conversion you have made. Built over years. Yours."
          className="mb-12"
        />

        <div className="mb-6 flex items-center justify-between gap-3">
          <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
            {total} conversion{total === 1 ? '' : 's'}
          </p>
          {selectionMode ? (
            <Button variant="secondary" size="sm" onClick={exitSelectionMode}>
              Cancel selection
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={enterSelectionMode}>
              Add to a feed
            </Button>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-8 p-6 bg-destructive/10 border border-destructive/20 rounded-sm animate-fade-in">
            <p className="font-body text-destructive mb-3">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchConversions(1)}
            >
              Try Again
            </Button>
          </div>
        )}

        {playbackError && (
          <div
            role="alert"
            className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start justify-between gap-4 animate-fade-in"
          >
            <p className="font-body text-sm text-destructive">{playbackError}</p>
            <button
              type="button"
              onClick={() => setPlaybackError(null)}
              className="font-mono text-xs tracking-widest uppercase text-muted-foreground hover:text-foreground"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Library List */}
        <div className="animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <LibraryList
            conversions={conversions}
            loading={loading}
            total={total}
            currentPage={currentPage}
            pageSize={pageSize}
            playingId={playingConversion?.id ?? null}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onPageChange={handlePageChange}
            onPlay={handlePlay}
            onDelete={deleteConversion}
            onToggleSelect={toggleSelect}
            onReported={(id) => void refreshConversion(id)}
            onRenewed={(id) => void refreshConversion(id)}
            onRenewError={(message) => setPlaybackError(message)}
          />
        </div>

        {/* Empty State CTA */}
        {!loading && conversions.length === 0 && (
          <div className="text-center mt-8 animate-fade-in">
            <Link to="/dashboard">
              <Button variant="primary">Add a source</Button>
            </Link>
          </div>
        )}
      </div>

      {/* Docked Audio Player */}
      {isPlayerVisible && !showSelectionToolbar && (
        <AudioPlayer
          key={playingAudioUrl}
          src={playingAudioUrl!}
          title={playingConversion!.title || 'Untitled conversion'}
          subtitle={playingConversion!.sourceUrl}
          onClose={() => { setPlayingConversion(null); setPlayingAudioUrl(null); }}
        />
      )}

      {/* Selection toolbar */}
      {showSelectionToolbar && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
              {selectedIds.size} selected
            </p>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={exitSelectionMode}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={selectedIds.size === 0}
                onClick={() => setFeedPickerOpen(true)}
              >
                Add to feed
              </Button>
            </div>
          </div>
        </div>
      )}

      <AddToFeedModal
        isOpen={feedPickerOpen}
        conversionIds={Array.from(selectedIds)}
        onClose={() => setFeedPickerOpen(false)}
        onAdded={() => {
          setFeedPickerOpen(false);
          exitSelectionMode();
        }}
      />
    </Layout>
  );
}
